import { useEffect } from 'react';
import { getContrastTextColor } from '@vibesites/tokens';

const STYLE_ID = 'comfy-cta';

// Same fallback chain as `.comfy-cta:hover` / footer / header CTA hovers.
const HOVER_BG_CSS = 'var(--comfy-cta-bg-hover, var(--vibe-color-surface, #f5f5f5))';

// Rounded, heading-font filled CTA — matches the Footer's Resident Portal /
// Apply Now buttons so every button-style CTA across the theme reads as one
// family. Solid secondary fill swaps to the surface color on hover. Colors are
// overridable via CSS custom properties so a section that already sits ON the
// secondary color (e.g. NeighborhoodSection) can flip the fill to something
// that stays legible against its background.
//
// Hover text color (--comfy-cta-fg-hover) is computed at runtime from the
// resolved hover background so light surfaces get dark text and dark surfaces
// get white text, keeping CTAs readable across property brand palettes.
const COMFY_CTA_CSS = `
.comfy-cta {
  display: inline-flex;
  max-width: 100%;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  padding: 1.1rem 1.5rem;
  font-family: var(--vibe-font-heading, var(--vibe-font-body, sans-serif));
  font-size: 1.1rem;
  font-weight: 500;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  background: var(--comfy-cta-bg, var(--vibe-color-secondary, #c9a961));
  color: var(--comfy-cta-fg, var(--vibe-color-text-on-secondary, #ffffff));
  border-radius: 10px;
  transition: background-color 0.25s ease, color 0.25s ease, opacity 0.25s ease;
}
.comfy-cta:hover {
  background: ${HOVER_BG_CSS};
  color: var(--comfy-cta-fg-hover, #1a1a1a);
}
`;

/** Parse `rgb()` / `rgba()` / hex into a `#rrggbb` string for contrast checks. */
function toHexColor(cssColor: string): string | null {
  const trimmed = cssColor.trim();
  if (!trimmed || trimmed === 'transparent') return null;

  if (trimmed.startsWith('#')) {
    let hex = trimmed.slice(1);
    if (hex.length === 3 || hex.length === 4) {
      hex = hex
        .slice(0, 3)
        .split('')
        .map((c) => c + c)
        .join('');
    } else if (hex.length === 8) {
      hex = hex.slice(0, 6);
    }
    return hex.length === 6 ? `#${hex.toLowerCase()}` : null;
  }

  const rgbMatch = trimmed.match(
    /^rgba?\(\s*([\d.]+)[%]?\s*[,/\s]\s*([\d.]+)[%]?\s*[,/\s]\s*([\d.]+)[%]?(?:\s*[,/]\s*[\d.%]+)?\s*\)$/i,
  );
  if (!rgbMatch) return null;

  const [r, g, b] = rgbMatch.slice(1, 4).map((n) => Math.round(Number(n)));
  if ([r, g, b].some((n) => Number.isNaN(n) || n < 0 || n > 255)) return null;
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Resolve any CSS color (hex, rgb, named, or `var(...)`) to a concrete hex via
 * the browser's computed style, then return WCAG contrast text (white or dark).
 */
function contrastTextForCssColor(cssColor: string): string {
  const direct = toHexColor(cssColor);
  if (direct) return getContrastTextColor(direct);

  if (typeof document === 'undefined') {
    return '#1a1a1a';
  }

  const host = document.body ?? document.documentElement;
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:absolute;left:-9999px;top:0;width:1px;height:1px;pointer-events:none;visibility:hidden;';
  probe.style.backgroundColor = cssColor;
  host.appendChild(probe);
  const resolved = getComputedStyle(probe).backgroundColor;
  host.removeChild(probe);

  const hex = toHexColor(resolved);
  return hex ? getContrastTextColor(hex) : '#1a1a1a';
}

/**
 * Resolve the effective CTA hover background through the same `var()` fallback
 * chain the buttons use, so token / editor overrides stay in sync.
 */
function resolveComfyCtaHoverBackground(): string {
  if (typeof document === 'undefined') return '#f5f5f5';
  const host = document.body ?? document.documentElement;
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:absolute;left:-9999px;top:0;width:1px;height:1px;pointer-events:none;visibility:hidden;';
  probe.style.background = HOVER_BG_CSS;
  host.appendChild(probe);
  const resolved = getComputedStyle(probe).backgroundColor;
  host.removeChild(probe);
  return resolved || '#f5f5f5';
}

/**
 * Read the effective CTA hover background and publish a matching
 * `--comfy-cta-fg-hover` so every CTA that shares the surface-hover pattern
 * stays readable when property surface / brand colors change.
 */
export function syncComfyCtaHoverForeground(
  styleEl: HTMLStyleElement,
  root: HTMLElement = document.documentElement,
): void {
  const hoverBg = resolveComfyCtaHoverBackground();
  const hoverFg = contrastTextForCssColor(hoverBg);

  // Inline on :root so it wins over stale stylesheet copies and stays visible
  // to every CTA selector (`.comfy-cta`, footer, header, lead-form, etc.).
  if (root.style.getPropertyValue('--comfy-cta-fg-hover') !== hoverFg) {
    root.style.setProperty('--comfy-cta-fg-hover', hoverFg);
  }

  if (styleEl.textContent !== COMFY_CTA_CSS) {
    styleEl.textContent = COMFY_CTA_CSS;
  }
}

/**
 * Injects the flat "Comfy" CTA button CSS into <head> exactly once and keeps
 * `--comfy-cta-fg-hover` in sync with the hover background's luminance.
 * Pair with the `comfy-cta` class on the anchor/button. Override the resting or
 * hover colors on a parent/element via the `--comfy-cta-bg`,
 * `--comfy-cta-fg`, `--comfy-cta-bg-hover`, and `--comfy-cta-fg-hover`
 * custom properties when the default secondary→surface swap would clash with
 * the surrounding surface. Prefer letting `--comfy-cta-fg-hover` stay auto-
 * computed; only set it manually when an element uses a different hover bg.
 */
export function useComfyCtaStyles(): void {
  useEffect(() => {
    let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = COMFY_CTA_CSS;
      document.head.appendChild(style);
    }

    const root = document.documentElement;
    // Seed a light-surface fallback before the first resolved sync.
    if (!root.style.getPropertyValue('--comfy-cta-fg-hover')) {
      root.style.setProperty('--comfy-cta-fg-hover', '#1a1a1a');
    }
    syncComfyCtaHoverForeground(style, root);

    // Property / editor token updates rewrite injected `:root` style tags or
    // `:root` inline styles — watch both so hover contrast stays current.
    let raf = 0;
    const scheduleSync = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        syncComfyCtaHoverForeground(style!, root);
      });
    };
    const observer = new MutationObserver(scheduleSync);
    observer.observe(root, { attributes: true, attributeFilter: ['style', 'class'] });
    observer.observe(document.head, { childList: true, subtree: true, characterData: true });

    return () => {
      observer.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
}
